import { useState } from 'react'
import { RefreshCw, FolderSearch, FileImage, FileText, Camera } from 'lucide-react'
import { useGetFilesQuery, useGetFilesSummaryQuery } from '@/store/api'
import PipelineIndicator from '@/components/files/PipelineIndicator'

type StatusFilter = 'all' | 'complete' | 'partial' | 'failed'

function FilesPage() {
  const [page, setPage] = useState(1)
  const [pageSize] = useState(50)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')

  const { data: summary, isLoading: summaryLoading, refetch: refetchSummary } = useGetFilesSummaryQuery()
  const { data: fileList, isLoading: filesLoading, refetch: refetchFiles } = useGetFilesQuery({
    page,
    pageSize,
    status: statusFilter === 'all' ? undefined : statusFilter,
  })

  const handleRefresh = () => {
    refetchSummary()
    refetchFiles()
  }

  const classificationIcon = (cls: string | null) => {
    switch (cls) {
      case 'photo': return <Camera size={14} className="text-blue-400" />
      case 'screenshot': return <FileImage size={14} className="text-purple-400" />
      case 'slide_doc':
      case 'text_document':
      case 'other_text_heavy': return <FileText size={14} className="text-yellow-400" />
      default: return <FileImage size={14} className="text-muted-foreground" />
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Files</h1>
        <div className="flex items-center gap-2">
          <button className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 hover:bg-primary/20 text-primary rounded-md text-sm">
            <FolderSearch size={16} />
            Scan Directory
          </button>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-3 py-1.5 hover:bg-secondary rounded-md text-sm"
          >
            <RefreshCw size={16} className={summaryLoading || filesLoading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-6">
        <SummaryCard label="Total" value={summary?.total ?? 0} />
        <SummaryCard label="Complete" value={summary?.fully_processed ?? 0} color="text-green-500" />
        <SummaryCard label="Partial" value={summary?.partially_processed ?? 0} color="text-yellow-500" />
        <SummaryCard label="Needs Caption" value={summary?.needs_caption ?? 0} color="text-orange-400" />
        <SummaryCard label="Needs Tags" value={summary?.needs_tags ?? 0} color="text-orange-400" />
      </div>

      <div className="border-t border-border my-6" />

      {/* Filter Bar */}
      <div className="flex items-center gap-3 mb-4">
        <span className="text-sm text-muted-foreground">Filter:</span>
        {(['all', 'complete', 'partial', 'failed'] as StatusFilter[]).map((f) => (
          <button
            key={f}
            onClick={() => { setStatusFilter(f); setPage(1) }}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              statusFilter === f
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-secondary text-muted-foreground'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Pipeline Stage Legend */}
      <div className="flex items-center gap-4 mb-4 text-xs text-muted-foreground">
        <span>Stages:</span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-green-500" /> Complete
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full border border-muted-foreground/40" /> Pending
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-secondary/50" /> N/A
        </span>
        <span className="ml-2">[A]nalyze [N]orm [C]lassify [E]mbed col[P]ali [F]aces [Ca]ption [T]ags</span>
      </div>

      {/* Files Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-secondary">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-medium">File</th>
              <th className="text-left px-4 py-3 text-sm font-medium">Type</th>
              <th className="text-left px-4 py-3 text-sm font-medium">Pipeline</th>
              <th className="text-right px-4 py-3 text-sm font-medium">Size</th>
              <th className="text-right px-4 py-3 text-sm font-medium">Tags</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filesLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  Loading files...
                </td>
              </tr>
            )}
            {!filesLoading && fileList?.items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  No files found
                </td>
              </tr>
            )}
            {fileList?.items.map((file) => (
              <tr key={file.master_id} className="hover:bg-secondary/50">
                <td className="px-4 py-2.5">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium truncate max-w-[240px]" title={file.filename}>
                      {file.filename}
                    </span>
                    <span className="text-xs text-muted-foreground font-mono">{file.master_id}</span>
                  </div>
                </td>
                <td className="px-4 py-2.5">
                  <span className="flex items-center gap-1.5">
                    {classificationIcon(file.classification)}
                    <span className="text-sm text-muted-foreground">
                      {file.classification || 'unknown'}
                    </span>
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  <PipelineIndicator stages={file.pipeline_stages} />
                </td>
                <td className="px-4 py-2.5 text-right text-sm text-muted-foreground">
                  {formatBytes(file.file_size_bytes)}
                </td>
                <td className="px-4 py-2.5 text-right text-sm text-muted-foreground">
                  {file.tag_count > 0 ? file.tag_count : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {fileList && fileList.total > pageSize && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-muted-foreground">
            Showing {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, fileList.total)} of {fileList.total}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 text-sm hover:bg-secondary rounded-md disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-muted-foreground">Page {page}</span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={!fileList.has_more}
              className="px-3 py-1.5 text-sm hover:bg-secondary rounded-md disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function SummaryCard({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <div className="bg-card border border-border rounded-lg p-3 text-center">
      <div className={`text-2xl font-bold ${color || ''}`}>{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </div>
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default FilesPage
